import {
  FINANCIAL_STATEMENT_DOC_TYPE_ID,
  FINANCIAL_SUBITEM_DESCRIPTORS,
  type FinancialSubItemDescriptor,
  normalizeDocumentLabel,
} from "helpers/financialDocumentConstants";
import type { CompanyDoc } from "types";

export const DIRECTOR_ID_SLOT = "director-id-doc";
export const SIGNATORY_ID_SLOT = "signatory-id-doc";
export const REGISTERED_ADDRESS_SLOT = "registered-address-proof";

export type UploadBoxItem = {
  id: string;
  label?: string;
  items?: Array<{ id: string; label?: string; name?: string }>;
};

export type UploadBoxSubItem = {
  id: string;
  label?: string;
  name?: string;
};

export const normalizeUploadBoxLabel = normalizeDocumentLabel;

const normalizeDocumentSlotToken = (value?: string) =>
  normalizeUploadBoxLabel(value)
    .replace(/billoflading/g, "bill_of_lading")
    .replace(/billlading/g, "bill_of_lading")
    .replace(/bl\b/g, "bill_of_lading");

const matchesFinancialSubItem = (
  sub: UploadBoxSubItem | undefined,
  descriptor: FinancialSubItemDescriptor,
): boolean =>
  Boolean(
    sub?.id === descriptor.id ||
      normalizeUploadBoxLabel(sub?.label).includes(descriptor.labelKeyword) ||
      normalizeUploadBoxLabel(sub?.name).includes(descriptor.labelKeyword),
  );

export const isAuditedFinancialSubItem = (sub?: UploadBoxSubItem): boolean =>
  matchesFinancialSubItem(sub, FINANCIAL_SUBITEM_DESCRIPTORS.audited);

export const isManagementFinancialSubItem = (sub?: UploadBoxSubItem): boolean =>
  matchesFinancialSubItem(sub, FINANCIAL_SUBITEM_DESCRIPTORS.management);

export const isFinancialDocumentsUploadGroup = (
  item: UploadBoxItem,
): boolean => {
  const hasAuditedSubItem = item.items?.some((sub) =>
    isAuditedFinancialSubItem(sub),
  );
  const hasManagementSubItem = item.items?.some((sub) =>
    isManagementFinancialSubItem(sub),
  );
  const normalizedLabel = normalizeUploadBoxLabel(item.label);

  return (
    normalizedLabel.includes("financialstatement") ||
    normalizedLabel.includes("financialdocuments") ||
    (Boolean(hasAuditedSubItem) && Boolean(hasManagementSubItem))
  );
};

export const buildUploadBoxItemIds = (item: UploadBoxItem): string[] => {
  const subItemIds = item.items?.map((sub) => sub.id) ?? [];
  return subItemIds.length > 0 ? [...subItemIds, item.id] : [item.id];
};

export const isValidIdDocumentTypeName = (name: string): boolean => {
  const normalizedName = (name || "").trim().toLowerCase();
  if (!normalizedName) {
    return false;
  }

  return (
    normalizedName.includes("passport") ||
    normalizedName.includes("identity card") ||
    normalizedName.includes("identification card") ||
    normalizedName.includes("national id") ||
    (normalizedName.includes("pan") && normalizedName.includes("card")) ||
    (normalizedName.includes("aadhar") && normalizedName.includes("card")) ||
    (normalizedName.includes("aadhaar") && normalizedName.includes("card")) ||
    normalizedName.includes("driving license") ||
    normalizedName.includes("driving licence")
  );
};

export const isLetterOfAuthorization = (name: string): boolean => {
  const normalizedName = (name || "").trim().toLowerCase();
  return (
    (normalizedName.includes("letter") &&
      normalizedName.includes("authorization")) ||
    (normalizedName.includes("letter") &&
      normalizedName.includes("authorisation"))
  );
};

export const isAuthorisedSignatoryIdDocumentName = (name: string): boolean => {
  const normalizedName = normalizeUploadBoxLabel(name);
  return (
    normalizedName.includes("id") &&
    normalizedName.includes("passport") &&
    (normalizedName.includes("authorised") ||
      normalizedName.includes("authorized")) &&
    (normalizedName.includes("signator") ||
      normalizedName.includes("signatory") ||
      normalizedName.includes("signatories"))
  );
};

export const isUserSignatoryRequiredDocumentName = (name: string): boolean =>
  isLetterOfAuthorization(name) || isAuthorisedSignatoryIdDocumentName(name);

export const shouldExcludeCompanyDocFromMapDocuments = (
  displayName: string,
): boolean => isUserSignatoryRequiredDocumentName(displayName);

export const isProofOfAddressDocumentName = (name: string): boolean =>
  normalizeUploadBoxLabel(name).includes("proofofaddress");

export const filterMapUserRequiredDocuments = <
  T extends { display_name: string },
>(
  userDocs: T[],
  accountType?: "supplier" | "buyer",
): T[] =>
  userDocs.filter((doc) => {
    if (isUserSignatoryRequiredDocumentName(doc.display_name)) {
      return true;
    }

    return (
      accountType === "buyer" && isProofOfAddressDocumentName(doc.display_name)
    );
  });

const isLikelyIdDocumentStorageCode = (code: string): boolean => {
  const normalizedCode = code.toLowerCase();
  return (
    normalizedCode.includes("passport") ||
    normalizedCode.includes("pan") ||
    normalizedCode.includes("aadhaar") ||
    normalizedCode.includes("aadhar") ||
    normalizedCode.includes("driving") ||
    normalizedCode.includes("license") ||
    normalizedCode.includes("licence")
  );
};

export const shouldRemapToDirectorIdSlot = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name">,
): boolean => {
  if (doc.document_type === SIGNATORY_ID_SLOT) {
    return false;
  }

  if (doc.document_type === DIRECTOR_ID_SLOT) {
    return true;
  }

  const hasTypeName = Boolean((doc.document_type_name ?? "").trim());
  if (hasTypeName) {
    return isValidIdDocumentTypeName(doc.document_type_name || "");
  }

  return isLikelyIdDocumentStorageCode(doc.document_type);
};

export const shouldDisplayInSignatoryIdSlot = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name">,
): boolean => {
  if (doc.document_type !== SIGNATORY_ID_SLOT) {
    return false;
  }

  const hasTypeName = Boolean((doc.document_type_name ?? "").trim());
  if (!hasTypeName) {
    return true;
  }

  return (
    isValidIdDocumentTypeName(doc.document_type_name || "") ||
    isLetterOfAuthorization(doc.document_type_name || "")
  );
};

export const remapUploadedIdDocumentSlot = (
  doc: CompanyDoc,
  currentDocIds: Set<string>,
): CompanyDoc => {
  if (currentDocIds.has(doc.document_type)) {
    return doc;
  }

  if (
    currentDocIds.has(SIGNATORY_ID_SLOT) &&
    shouldDisplayInSignatoryIdSlot(doc)
  ) {
    return { ...doc, document_type: SIGNATORY_ID_SLOT };
  }

  if (currentDocIds.has(DIRECTOR_ID_SLOT) && shouldRemapToDirectorIdSlot(doc)) {
    return { ...doc, document_type: DIRECTOR_ID_SLOT };
  }

  return doc;
};

const isRegisteredAddressDocument = (
  doc: Pick<CompanyDoc, "document_type_name" | "required_document_id">,
): boolean => {
  const requiredDocumentId = (doc.required_document_id ?? "").toLowerCase();
  const typeName = (doc.document_type_name ?? "").toLowerCase();

  return (
    requiredDocumentId === "proof-company-address" ||
    requiredDocumentId === REGISTERED_ADDRESS_SLOT ||
    typeName.includes("proof of company address") ||
    typeName.includes("proof of registered address")
  );
};

export const remapUploadedDocumentSlot = (
  doc: CompanyDoc,
  currentDocIds: Set<string>,
): CompanyDoc => {
  if (
    currentDocIds.has(REGISTERED_ADDRESS_SLOT) &&
    isRegisteredAddressDocument(doc)
  ) {
    return { ...doc, document_type: REGISTERED_ADDRESS_SLOT };
  }

  return remapUploadedIdDocumentSlot(doc, currentDocIds);
};

export const documentMatchesUploadBox = (
  doc: Pick<CompanyDoc, "document_type" | "document_type_name"> & {
    requiredDocId?: string;
    map_invoice_required_document_id?: string;
  },
  item: UploadBoxItem,
  boxItemId: string,
  allItemIds: string[],
): boolean => {
  const uploadBoxTokens = new Set(
    [
      boxItemId,
      item.label,
      ...allItemIds,
      ...(item.items?.flatMap((sub) => [sub.id, sub.label, sub.name]) ?? []),
    ]
      .filter(Boolean)
      .map((value) => normalizeDocumentSlotToken(String(value))),
  );
  const documentTokens = new Set(
    [
      doc.document_type,
      doc.document_type_name,
      doc.requiredDocId,
      doc.map_invoice_required_document_id,
    ]
      .filter(Boolean)
      .map((value) => normalizeDocumentSlotToken(String(value))),
  );

  let matches =
    allItemIds.includes(doc.document_type) ||
    Array.from(documentTokens).some((token) => uploadBoxTokens.has(token));

  if (!matches && boxItemId === DIRECTOR_ID_SLOT) {
    const hasTypeName = Boolean((doc.document_type_name ?? "").trim());
    matches =
      !hasTypeName || isValidIdDocumentTypeName(doc.document_type_name || "");
  }

  if (!matches) {
    return false;
  }

  if (
    doc.document_type === DIRECTOR_ID_SLOT ||
    (boxItemId === DIRECTOR_ID_SLOT && doc.document_type !== SIGNATORY_ID_SLOT)
  ) {
    const hasTypeName = Boolean((doc.document_type_name ?? "").trim());
    return hasTypeName
      ? isValidIdDocumentTypeName(doc.document_type_name || "")
      : true;
  }

  if (doc.document_type === SIGNATORY_ID_SLOT) {
    const hasTypeName = Boolean((doc.document_type_name ?? "").trim());
    if (!hasTypeName) {
      return true;
    }

    return (
      isValidIdDocumentTypeName(doc.document_type_name || "") ||
      isLetterOfAuthorization(doc.document_type_name || "")
    );
  }

  return true;
};

const dedupeDocumentsById = (docs: CompanyDoc[]): CompanyDoc[] => {
  const seenDocIds = new Set<string>();
  return docs.filter((doc) => {
    if (seenDocIds.has(doc.id)) {
      return false;
    }
    seenDocIds.add(doc.id);
    return true;
  });
};

export const filterDocumentsForUploadBox = (
  item: UploadBoxItem,
  uploadedDocs: CompanyDoc[],
): CompanyDoc[] => {
  const allItemIds = buildUploadBoxItemIds(item);
  const isFinancialGroup = isFinancialDocumentsUploadGroup(item);

  const filtered = uploadedDocs.filter((doc) => {
    if (documentMatchesUploadBox(doc as any, item, item.id, allItemIds)) {
      return true;
    }

    return (
      isFinancialGroup && doc.document_type === FINANCIAL_STATEMENT_DOC_TYPE_ID
    );
  });

  return dedupeDocumentsById(filtered);
};

export const resolveFinancialGroupDisplaySlotId = (
  document: Pick<CompanyDoc, "document_type" | "document_type_name">,
  item: UploadBoxItem,
): string => {
  if (
    document.document_type === FINANCIAL_STATEMENT_DOC_TYPE_ID &&
    isFinancialDocumentsUploadGroup(item)
  ) {
    return normalizeUploadBoxLabel(document.document_type_name).includes(
      "management",
    )
      ? FINANCIAL_SUBITEM_DESCRIPTORS.management.id
      : FINANCIAL_SUBITEM_DESCRIPTORS.audited.id;
  }

  return document.document_type;
};
